/**
 * useLeaderboard Hook
 *
 * The global leaderboard from the canonical backend endpoint
 * (GET /gamification/leaderboard). Participation is opt-in and enforced on the
 * server: only users whose public profile is public and shows authority scores,
 * and who opted in to talent discovery, are listed. The user's own opt-in state is read from their persisted public
 * profile settings — never from local storage.
 */

import { useMemo } from 'react';
import { useAuth } from './useAuth';
import {
  useGetLeaderboardQuery,
  useGetMyGamificationOverviewQuery,
} from '../store/api/gamificationApi';
import { useGetMyPublicProfileSettingsQuery } from '../store/api/portfolioApi';
import { useGetMyDiscoveryQuery } from '../store/api/sharingApi';
import {
  findCurrentUserRank,
  isLeaderboardOptedIn,
  toLeaderboardEntries,
} from '../utils/leaderboard';

export interface UseLeaderboardOptions {
  limit?: number;
}

export function useLeaderboard(options: UseLeaderboardOptions = {}) {
  const { limit = 50 } = options;
  const { user } = useAuth();

  const {
    data: board,
    isLoading: isBoardLoading,
    error,
  } = useGetLeaderboardQuery({ limit });

  const { data: settings, isLoading: isSettingsLoading } = useGetMyPublicProfileSettingsQuery();
  const { data: discovery, isLoading: isDiscoveryLoading, isError: discoveryFailed } = useGetMyDiscoveryQuery();
  const { data: overview, isLoading: isOverviewLoading } = useGetMyGamificationOverviewQuery();

  const entries = useMemo(() => toLeaderboardEntries(board?.items, user?.id), [board, user?.id]);
  const currentUserRank = useMemo(() => findCurrentUserRank(entries, user?.id), [entries, user?.id]);
  // A failed discovery read counts as not opted in (the server fails closed too).
  const isOptedIn = isLeaderboardOptedIn(settings, discoveryFailed ? null : discovery);

  // The user's own numbers, from the canonical overview. avgScore stays null
  // when nothing was measured — render it as "—", never 0.
  const currentUserStats = overview
    ? { avgScore: overview.stats.avgScore, sessionsCount: overview.stats.totalSessions }
    : null;

  return {
    entries,
    currentUserRank,
    currentUserStats,
    isOptedIn,
    isLoading: isBoardLoading || isSettingsLoading || isDiscoveryLoading || isOverviewLoading,
    error,
  };
}
